import {Brand, BrandKey, brands, useGlobalFeatures} from '@ark/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {userContext} from '@ark/web';
import {BrandAssets, brandAssets} from './brandAssets';
import {isBtlHost} from './isBtlHost';

const STORAGE_KEY = 'brand';
const BTL_SESSION_KEY = 'brandBtlSession';
const BRAND_FLAG_TIMEOUT_MS = 4000;

type BrandContextType = Brand & {
  assets: BrandAssets;
  resolved: boolean;
  confirmed: boolean;
  setBtlSession: (isBtl: boolean) => void;
};

const readStoredBrand = (): BrandKey | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'cortile' || stored === 'ark' ? stored : null;
  } catch {
    return null;
  }
};

const storeBrand = (key: BrandKey) => {
  try {
    localStorage.setItem(STORAGE_KEY, key);
  } catch {
    return;
  }
};

const readStoredBtlSession = () => {
  try {
    return localStorage.getItem(BTL_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const storeBtlSession = (isBtl: boolean) => {
  try {
    localStorage.setItem(BTL_SESSION_KEY, String(isBtl));
  } catch {
    return;
  }
};

const toBrandKey = (features: Record<string, object>): BrandKey =>
  features.cortileBrand ? 'cortile' : 'ark';

const setHeadLink = (rel: string, href: string) => {
  const link =
    document.querySelector<HTMLLinkElement>(`link[rel='${rel}']`) ??
    document.head.appendChild(
      Object.assign(document.createElement('link'), {rel}),
    );
  if (link.getAttribute('href') !== href) link.href = href;
};

const setHeadMeta = (name: string, content: string) => {
  const meta = document.querySelector<HTMLMetaElement>(`meta[name='${name}']`);
  if (meta) meta.content = content;
};

const setHeadProperty = (property: string, content: string) => {
  const meta = document.querySelector<HTMLMetaElement>(
    `meta[property='${property}']`,
  );
  if (meta) meta.content = content;
};

const BrandContext = createContext<BrandContextType>({
  ...brands.ark,
  assets: brandAssets.ark,
  resolved: true,
  confirmed: true,
  setBtlSession: () => undefined,
});

export const BrandProvider = ({children}: {children: ReactNode}) => {
  const [features, loading] = useGlobalFeatures();
  const {isLoggedIn} = useContext(userContext);
  const [isBtlSession, setIsBtlSession] = useState(readStoredBtlSession);
  const [storedKey] = useState(readStoredBrand);
  const [flagTimedOut, setFlagTimedOut] = useState(false);
  const [confirmTimedOut, setConfirmTimedOut] = useState(false);

  const flagKey: BrandKey | null = loading ? null : toBrandKey(features);

  useEffect(() => {
    if (flagKey) storeBrand(flagKey);
  }, [flagKey]);

  const forcedArk = (isLoggedIn && isBtlSession) || isBtlHost() || flagTimedOut;
  const resolvedKey: BrandKey | null = forcedArk
    ? 'ark'
    : (flagKey ?? storedKey);
  const key: BrandKey = resolvedKey ?? 'ark';
  const resolved = resolvedKey !== null;
  const confirmed = forcedArk || flagKey !== null || confirmTimedOut;

  useEffect(() => {
    if (confirmed) return;
    const timeoutId = window.setTimeout(
      () => setConfirmTimedOut(true),
      BRAND_FLAG_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [confirmed]);

  useEffect(() => {
    if (resolvedKey !== null) return;
    const timeoutId = window.setTimeout(
      () => setFlagTimedOut(true),
      BRAND_FLAG_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeoutId);
  }, [resolvedKey]);

  useLayoutEffect(() => {
    if (!resolved) return;
    const brand = brands[key];
    const assets = brandAssets[key];
    document.title = brand.name;
    setHeadMeta('apple-mobile-web-app-title', brand.name);
    setHeadMeta('description', assets.description);
    setHeadMeta('theme-color', assets.themeColor);
    setHeadProperty('og:site_name', brand.name);
    setHeadProperty('og:title', brand.name);
    setHeadProperty('og:description', assets.description);
    setHeadLink('icon', assets.favicon);
    setHeadLink('manifest', assets.manifest);
    setHeadLink('apple-touch-icon', assets.touchIcon);
  }, [key, resolved]);

  const setBtlSession = useCallback((isBtl: boolean) => {
    storeBtlSession(isBtl);
    setIsBtlSession(isBtl);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) setBtlSession(false);
  }, [isLoggedIn, setBtlSession]);

  const value = useMemo(
    () => ({
      ...brands[key],
      assets: brandAssets[key],
      resolved,
      confirmed,
      setBtlSession,
    }),
    [key, resolved, confirmed, setBtlSession],
  );

  return (
    <BrandContext.Provider value={value}>{children}</BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);
